import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import i18n from '../../i18n/i18n';
import { APP_STATUS } from '../../constants';
import {
  IPaymentSessionState,
  IActiveSession,
  ISessionSummary,
} from '../../interface/paymentSession.interface';
// Import async thunks from payment.action.ts
import { getActiveSession, startSession, endSession } from '../action/payment.action';

const initialState: IPaymentSessionState = {
  activeSession: null,
  sessionSummary: null,

  // Loading states
  loadingStart: false,
  loadingEnd: false,
  loadingActive: false,

  // Error states
  errorStart: null,
  errorEnd: null,
  errorActive: null,

  // General status
  status: APP_STATUS.IDLE,
  message: '',
};

const paymentSessionSlice = createSlice({
  name: 'paymentSession',
  initialState,
  reducers: {
    // Active Session Management
    setActiveSession: (state, action: PayloadAction<IActiveSession>) => {
      state.activeSession = action.payload;
      state.status = APP_STATUS.SUCCESS;
    },
    clearActiveSession: (state) => {
      state.activeSession = null;
      state.sessionSummary = null;
      state.status = APP_STATUS.IDLE;
    },

    // Session Summary
    setSessionSummary: (state, action: PayloadAction<ISessionSummary>) => {
      state.sessionSummary = action.payload;
    },

    // Start Session States
    setLoadingStart: (state, action: PayloadAction<boolean>) => {
      state.loadingStart = action.payload;
      if (action.payload) {
        state.errorStart = null;
        state.status = APP_STATUS.INITIAL;
      }
    },
    setErrorStart: (state, action: PayloadAction<string | null>) => {
      state.errorStart = action.payload;
      state.loadingStart = false;
      if (action.payload) {
        state.status = APP_STATUS.ERROR;
      }
    },

    // End Session States
    setLoadingEnd: (state, action: PayloadAction<boolean>) => {
      state.loadingEnd = action.payload;
      if (action.payload) {
        state.errorEnd = null;
        state.status = APP_STATUS.INITIAL;
      }
    },
    setErrorEnd: (state, action: PayloadAction<string | null>) => {
      state.errorEnd = action.payload;
      state.loadingEnd = false;
      if (action.payload) {
        state.status = APP_STATUS.ERROR;
      }
    },

    // Active Session States
    setLoadingActive: (state, action: PayloadAction<boolean>) => {
      state.loadingActive = action.payload;
      if (action.payload) {
        state.errorActive = null;
      }
    },
    setErrorActive: (state, action: PayloadAction<string | null>) => {
      state.errorActive = action.payload;
      state.loadingActive = false;
      if (action.payload) {
        state.status = APP_STATUS.ERROR;
      }
    },

    // General State
    setStatus: (state, action: PayloadAction<APP_STATUS>) => {
      state.status = action.payload;
    },
    setMessage: (state, action: PayloadAction<string>) => {
      state.message = action.payload;
    },

    // Reset State
    resetPaymentSessionState: () => initialState,
  },

  // Handle async thunks from payment.action.ts
  extraReducers: (builder) => {
    // Get Active Session
    builder
      .addCase(getActiveSession.pending, (state) => {
        state.loadingActive = true;
        state.errorActive = null;
      })
      .addCase(getActiveSession.fulfilled, (state, action) => {
        state.loadingActive = false;
        state.activeSession = action.payload; // Could be null
        state.status = action.payload ? APP_STATUS.SUCCESS : APP_STATUS.IDLE;
      })
      .addCase(getActiveSession.rejected, (state, action) => {
        state.loadingActive = false;
        state.errorActive =
          action.payload?.message ||
          i18n.t('toast.failed_to_fetch_active_session', 'Failed to fetch active session');
        state.status = APP_STATUS.ERROR;
      });

    // Start Session
    builder
      .addCase(startSession.pending, (state) => {
        state.loadingStart = true;
        state.errorStart = null;
        state.status = APP_STATUS.INITIAL;
      })
      .addCase(startSession.fulfilled, (state, action) => {
        state.loadingStart = false;
        state.activeSession = action.payload;
        state.status = APP_STATUS.SUCCESS;
      })
      .addCase(startSession.rejected, (state, action) => {
        state.loadingStart = false;
        state.errorStart =
          action.payload?.message ||
          i18n.t('toast.failed_to_start_session', 'Failed to start session');
        state.status = APP_STATUS.ERROR;
      });

    // End Session
    builder
      .addCase(endSession.pending, (state) => {
        state.loadingEnd = true;
        state.errorEnd = null;
        state.status = APP_STATUS.INITIAL;
      })
      .addCase(endSession.fulfilled, (state, action) => {
        state.loadingEnd = false;
        state.activeSession = action.payload.session;
        state.sessionSummary = action.payload.summary;
        state.status = APP_STATUS.SUCCESS;
      })
      .addCase(endSession.rejected, (state, action) => {
        state.loadingEnd = false;
        state.errorEnd =
          action.payload?.message || i18n.t('toast.failed_to_end_session', 'Failed to end session');
        state.status = APP_STATUS.ERROR;
      });
  },
});

export const {
  setActiveSession,
  clearActiveSession,
  setSessionSummary,
  setLoadingStart,
  setErrorStart,
  setLoadingEnd,
  setErrorEnd,
  setLoadingActive,
  setErrorActive,
  setStatus,
  setMessage,
  resetPaymentSessionState,
} = paymentSessionSlice.actions;

export default paymentSessionSlice.reducer;
